import React, { useEffect, useState, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'
import { fetchTutorialsRoutine } from 'features/tutorials/ducks/actions'
import {
  selectTutorials,
  selectTutorialsLoading,
  selectTutorialsError
} from 'features/tutorials/ducks/selectors'
import { ReactComponent as CloseIcon } from 'assets/images/close.svg'
import { ReactComponent as SearchIcon } from 'assets/images/search.svg'
import { ReactComponent as PlayIcon } from 'assets/images/play.svg'
import { useTutorial } from 'features/tutorials/context/TutorialContext'
import { isNewItem } from 'utils/isNewItem'
import { isOnline as checkIsOnline } from 'ducks/offline/actions'

const TutorialsPanel = ({ onClose }) => {
  const dispatch = useDispatch()
  const tutorials = useSelector(selectTutorials) || []
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)


  const [search, setSearch] = useState('')
  const [sidebarWidth, setSidebarWidth] = useState(0)
  const [seenTutorials, setSeenTutorials] = useState([])

  const { openTutorial } = useTutorial()
  const panelRef = useRef(null)

  useEffect(() => {
    if (!checkIsOnline()) {
      setLoading(false)
      setError('Tutorials are not available offline.')
      return
    }
    setLoading(true)
    setError(null)
    dispatch(fetchTutorialsRoutine())
  }, [dispatch])

  useEffect(() => {
    if (tutorials.length > 0) {
      setLoading(false)
    } else {
      const fallback = setTimeout(() => setLoading(false), 5000)
      return () => clearTimeout(fallback)
    }
  }, [tutorials])

  useEffect(() => {
    const stored = JSON.parse(localStorage.getItem('seen_tutorials_ids') || '[]')
    setSeenTutorials(stored)
  }, [])

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        onClose()
      }
    }

    const handleEsc = (e) => {
      if (e.key === 'Escape') onClose()
    }

    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleEsc)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEsc)
    }
  }, [onClose])

  useEffect(() => {
    const handleOpenFromWhatsNew = () => setSidebarWidth(76)
    const handleOpenFromButton = () => setSidebarWidth(0)

    window.addEventListener('openTutorialsPanel', handleOpenFromButton)
    window.addEventListener('openTutorialsPanelFromWhatsNew', handleOpenFromWhatsNew)

    return () => {
      window.removeEventListener('openTutorialsPanel', handleOpenFromButton)
      window.removeEventListener('openTutorialsPanelFromWhatsNew', handleOpenFromWhatsNew)
    }
  }, [])

  const handleOpenTutorial = (tutorial) => {
    openTutorial(tutorial)

    const id = tutorial.id || tutorial.uuid
    if (!id) return

    const seenIds = JSON.parse(localStorage.getItem('seen_tutorials_ids') || '[]')
    if (!seenIds.includes(id)) {
      const updated = Array.from(new Set([...seenIds, id]))
      localStorage.setItem('seen_tutorials_ids', JSON.stringify(updated))
      setSeenTutorials(updated)
    }

    const updatedSeen = JSON.parse(localStorage.getItem('seen_tutorials_ids') || '[]')

    const hasUnseenNew = tutorials.some(
      (t) => isNewItem(t, 'tutorials') && !updatedSeen.includes(t.id || t.uuid)
    )

    if (!hasUnseenNew && tutorials.length > 0) {
      localStorage.setItem('last_seen_tutorials', new Date().toISOString())
    }

    window.dispatchEvent(
      new CustomEvent('tutorials-updated', {
        detail: { hasUnseenNew },
      })
    )
  }

  useEffect(() => {
    if (!tutorials.length) return

    const unseenNew = tutorials.filter((t) => {
      const id = t.id || t.uuid
      return isNewItem(t, 'tutorials') && !seenTutorials.includes(id)
    })

    if (unseenNew.length === 0) {
      localStorage.setItem('last_seen_tutorials', new Date().toISOString())
    }
  }, [tutorials, seenTutorials])

  // sort + search
  const sorted = [...tutorials].sort((a, b) => (a.order || 9999) - (b.order || 9999))
  const filtered = sorted.filter((t) =>
    t?.title?.toLowerCase().includes(search.toLowerCase())
  )
  const featured = sorted.filter((t) => Boolean(t.is_featured || t.isFeatured))

  if (loading) {
    return (
      <PanelWrapper ref={panelRef}>
        <SkeletonContainer>
          <SkeletonLine width="70%" />
          <SkeletonLine width="90%" />
          <SkeletonLine width="60%" />
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
        </SkeletonContainer>
      </PanelWrapper>
    )
  }

  if (error) {
    return (
      <PanelWrapper ref={panelRef}>
        <Error>Failed to load tutorials</Error>
      </PanelWrapper>
    )
  }

  return (
    <PanelWrapper $left={sidebarWidth} ref={panelRef}>
      <Header>
        <Title>Tutorials</Title>
        <CloseButton onClick={onClose}>
          <CloseIcon />
        </CloseButton>
      </Header>

      {featured.length > 0 && (
        <>
          <SectionTitle>Latest</SectionTitle>
          <LatestGrid>
            {featured.map((t) => (
              <LatestCard key={t.id || t.uuid} onClick={() => handleOpenTutorial(t)}>
                <h3>{t.title}</h3>
                <p>{t.description}</p>
              </LatestCard>
            ))}
          </LatestGrid>
        </>
      )}

      <SearchBar>
        <SearchIcon />
        <input
          type="text"
          placeholder="Search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </SearchBar>

      <List>
        {filtered.map((t) => {
          const id = t.id || t.uuid
          const isNew = isNewItem(t, 'tutorials')
          const wasSeen = seenTutorials.includes(id)

          return (
            <Item key={id || t.title} onClick={() => handleOpenTutorial(t)}>
              <PlayIcon />
              <div>
                <HeaderLine>
                  <h4>{t.title}</h4>
                  {isNew && !wasSeen && <NewBadge>NEW</NewBadge>}
                </HeaderLine>
                <p>{t.description}</p>
              </div>
            </Item>
          )
        })}
      </List>
    </PanelWrapper>
  )
}

export default TutorialsPanel

const PanelWrapper = styled.div`
  position: fixed;
  top: 0;
  left: ${({ $left }) => `${$left}px`};
  width: 460px;
  bottom: 0;
  height: auto;
  background: #fafbfc;
  display: flex;
  flex-direction: column;
  padding: 25px 30px;
  box-sizing: border-box;
  overflow-y: auto;
  box-shadow: 6px 0 20px rgba(0, 0, 0, 0.1);
  border-top-right-radius: 18px;
  border-bottom-right-radius: 18px;
  z-index: 9999;
  -webkit-overflow-scrolling: touch;
  animation: slideIn 0.3s ease;

  @keyframes slideIn {
    from { transform: translateX(-100%); opacity: 0; }
    to { transform: translateX(0); opacity: 1; }
  }

  @media (max-width: 768px) {
    width: 100%;
    max-width: 100vw;
    left: 0 !important;
    border-radius: 0;
    padding: 20px 16px;
    box-shadow: none;
    top: 0;
    bottom: 0;
    height: 100%;
    height: -webkit-fill-available;
  }
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-right: 10px;
`

const Title = styled.h2`
  font-size: 24px;
  margin: 0;
  font-weight: 700;

  @media (max-width: 480px) {
    font-size: 20px;
  }
`

const CloseButton = styled.button`
  border: none;
  background: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 23px;
    height: 23px;
    stroke-width: 2;
  }

  &:hover svg {
    opacity: 0.8;
  }
`

const SectionTitle = styled.h3`
  font-size: 20px;
  margin: 20px 0;
  font-weight: 600;
`

const LatestGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;

  @media (max-width: 400px) {
    grid-template-columns: 1fr;
  }
`

const LatestCard = styled.div`
  background: #eaf0fb;
  border-radius: 10px;
  padding: 20px;
  cursor: pointer;
  transition: all 0.2s;
  &:hover { background: #dce6f7; }
  h3 { font-size: 18px; font-weight: 700; margin: 0 0 8px; }
  p { font-size: 13px; color: #555; margin: 0; }
`

const SearchBar = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  margin: 30px 0 20px;
  padding: 10px 15px;
  background: #fff;

  svg {
    width: 22px;
    height: 22px;
    opacity: 0.6;
  }

  input {
    border: none;
    outline: none;
    flex: 1;
    font-size: 16px;
    background: transparent;
  }
`

const List = styled.div`
  margin-top: 10px;
`

const Item = styled.div`
  background: #eaf0fb;
  border-radius: 10px;
  padding: 16px;
  margin-bottom: 14px;
  display: flex;
  align-items: flex-start;
  gap: 14px;
  cursor: pointer;
  transition: 0.2s all;

  &:hover { background: #dce6f7; }

  svg {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
    margin-top: 4px;
  }

  h4 {
    font-size: 18px;
    font-weight: 700;
    margin: 0 0 5px;
  }

  p {
    font-size: 13px;
    color: #555;
    margin: 0;
  }
`

const Loading = styled.div`
  margin: auto;
  text-align: center;
  font-size: 18px;
`

const Error = styled.div`
  margin: auto;
  text-align: center;
  color: red;
  font-size: 16px;
`

const HeaderLine = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const NewBadge = styled.span`
  background: #ff4d4f;
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 10px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
`

const SkeletonContainer = styled.div`
  margin-top: 30px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const SkeletonLine = styled.div`
  height: 18px;
  width: ${({ width }) => width || '100%'};
  background: linear-gradient(90deg, #f0f0f0 25%, #f7f7f7 37%, #f0f0f0 63%);
  border-radius: 4px;
  background-size: 400% 100%;
  animation: shimmer 1.4s ease infinite;

  @keyframes shimmer {
    0% { background-position: -400px 0; }
    100% { background-position: 400px 0; }
  }
`

const SkeletonCard = styled.div`
  height: 80px;
  border-radius: 10px;
  background: linear-gradient(90deg, #f0f0f0 25%, #f7f7f7 37%, #f0f0f0 63%);
  background-size: 400% 100%;
  animation: shimmer 1.4s ease infinite;
`

